import { useCallback, useEffect, useState } from 'react'

import { fetchIncidentHistory, type IncidentSummary, type StatusEntry } from '../data/public-status'
import { useSystemStatus } from '../data/use-status'
import { LATENCY_INTRO, LatencyCell, LatencyControl } from '../probe/latency'
import { useLatency } from '../probe/use-latency'
import { IncidentList } from '../status/incident-rows'
import { Overall } from '../status/overall'
import { StateList } from '../status/state-list'
import { Unavailable } from '../status/unavailable'
import { UpdatedAt } from '../status/updated-at'

/*
 * The status page, in V1_2_SPEC.md §4.1's order and nothing above the first
 * line:
 *
 *   overall status · active incident · components · regions · recent incidents
 *   · last updated
 *
 * ── Two requests, and the failure of one does not become the failure of both ──
 *
 * `GET /system/status` is the page. `GET /system/incidents` is the trailing
 * ninety days, and it is fetched separately and once: it changes on the order of
 * days, not seconds, and putting it on the 30-second poll would triple the
 * request rate for a list that will be identical every time. A failure of the
 * history is rendered *in the history section* — it must not present as the
 * platform being down, because it is not, and §5.3's headline condition is
 * specifically about the status read.
 */

export function Home() {
  const { status, failure, loading, stale, retry } = useSystemStatus()
  const { history, reload: reloadHistory } = useIncidentHistory()

  const unavailable = failure !== null

  /* One control, both requests. When the API is down both fail, and a "Retry"
     that silently fixed only the top of the page while the history stayed
     broken until a full reload is a button that does less than it says. */
  const retryEverything = useCallback(() => {
    retry()
    reloadHistory()
  }, [retry, reloadHistory])

  return (
    <>
      {unavailable ? (
        <div className="mb-6">
          <Unavailable
            failure={failure}
            onRetry={retryEverything}
            retrying={loading}
            beneath={status === null ? 'none' : stale ? 'demoted' : 'unconfirmed'}
          />
        </div>
      ) : null}

      {status === null ? (
        /*
         * No payload has ever arrived. There is nothing honest to render here —
         * not even a skeleton of component rows, because this app carries no copy
         * of the taxonomy (§4.1, D4): the names come down with the payload. So
         * the first load says it is loading, and a failed first load has already
         * said so above, in `Unavailable`.
         */
        loading ? (
          <p className="text-sm text-text-muted">Loading platform status…</p>
        ) : null
      ) : (
        <>
          <Overall
            state={status.overall}
            {...(status.activeIncidents.length > 0
              ? {
                  detail: `${String(status.activeIncidents.length)} open incident${
                    status.activeIncidents.length === 1 ? '' : 's'
                  }.`,
                }
              : {})}
          />

          {status.activeIncidents.length === 0 ? null : (
            <IncidentList
              title="Open incidents"
              incidents={status.activeIncidents}
              empty=""
            />
          )}

          <StateList
            title="Components"
            entries={status.components}
            empty="No component state was reported."
          />

          <Regions entries={status.regions} />

          <RecentIncidents history={history} active={status.activeIncidents} />

          <UpdatedAt isoInstant={status.updatedAt} stale={stale} />
        </>
      )}
    </>
  )
}

/* ── Regions, and the latency measured from this browser ─────────────────── */

/**
 * §4.1's regions row: "status + your latency".
 *
 * The state comes from the control plane and the latency from the visitor's own
 * browser, and the two are **not the same kind of fact** — which is why a region
 * that is not operational shows no number at all. A latency figure beside
 * "Not reporting" would invite the reading that the region is fine because it
 * answered a three-byte probe, when what the probe proves is only that one
 * hostname is reachable. §10.4 puts the state first for that reason.
 */
function Regions({ entries }: { readonly entries: readonly StatusEntry[] }) {
  const latency = useLatency(entries.map((entry) => entry.id))
  const operational = new Set(
    entries.filter((entry) => entry.state === 'operational').map((entry) => entry.id),
  )

  return (
    <StateList
      title="Regions"
      entries={entries}
      empty="No region state was reported."
      intro={LATENCY_INTRO}
      sectionRef={latency.ref}
      trailing={(code) => (
        <LatencyCell state={latency.state} code={code} operational={operational.has(code)} />
      )}
      footer={<LatencyControl state={latency.state} onRemeasure={latency.remeasure} />}
    />
  )
}

/* ── The trailing ninety days ────────────────────────────────────────────── */

type HistoryResult =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly incidents: readonly IncidentSummary[] }
  | { readonly kind: 'failed' }

function useIncidentHistory(): {
  readonly history: HistoryResult
  readonly reload: () => void
} {
  const [result, setResult] = useState<HistoryResult>({ kind: 'loading' })
  /* Bumping this re-runs the effect. The history is fetched **once**, not on the
     30-second poll — it changes on the order of days, and putting it on the poll
     would triple the request rate for a list that will be identical every time —
     so an explicit reload is the only other way it can be refetched. */
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => {
    setResult({ kind: 'loading' })
    setAttempt((current) => current + 1)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetchIncidentHistory(controller.signal)
      .then((incidents) => {
        if (!controller.signal.aborted) setResult({ kind: 'ready', incidents })
      })
      .catch(() => {
        if (!controller.signal.aborted) setResult({ kind: 'failed' })
      })
    return () => {
      controller.abort()
    }
  }, [attempt])

  return { history: result, reload }
}

/* Outside `Home` — FRONTEND_SPEC.md §5.1, `rerender-no-inline-components`. */
function RecentIncidents({
  history,
  active,
}: {
  readonly history: HistoryResult
  readonly active: readonly IncidentSummary[]
}) {
  if (history.kind === 'loading') {
    return (
      <section className="mt-8">
        <h2 className="type-label">Recent incidents</h2>
        <p className="mt-2 text-sm text-text-muted">Loading the last 90 days…</p>
      </section>
    )
  }

  if (history.kind === 'failed') {
    /*
     * The history failed and the status did not. Saying so precisely matters:
     * "an incident-feed failure must not silently become 'All systems
     * operational'" cuts both ways, and the inverse — a history failure
     * presented as an outage — would send a reader looking for a problem that is
     * not there. This says what could not be loaded and stops.
     */
    return (
      <section className="mt-8">
        <h2 className="type-label">Recent incidents</h2>
        <p className="mt-2 border border-border-default bg-surface-base px-4 py-3 text-sm text-text-muted">
          Incident history could not be loaded. This does not indicate the state of the platform —
          the component and region states above were reported separately.
        </p>
      </section>
    )
  }

  /* An open incident is already at the top of the page under "Open incidents".
     Listing it twice reads as two incidents. */
  const openReferences = new Set(active.map((incident) => incident.reference))
  const resolved = history.incidents.filter(
    (incident) => !openReferences.has(incident.reference),
  )

  return (
    <IncidentList
      title="Recent incidents"
      incidents={resolved}
      empty="No incidents in the last 90 days."
    />
  )
}
